import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  chargeTypesApi,
  ChargeCategoryExtended,
  RateUnit,
  CreateChargeTypeDto,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const CHARGE_CATEGORIES: { value: ChargeCategoryExtended; label: string }[] = [
  { value: 'Freight', label: 'Freight' },
  { value: 'OriginCharges', label: 'Origin Charges' },
  { value: 'DestinationCharges', label: 'Destination Charges' },
  { value: 'OriginHaulage', label: 'Origin Haulage' },
  { value: 'DestinationHaulage', label: 'Destination Haulage' },
  { value: 'ExportClearance', label: 'Export Clearance' },
  { value: 'ImportClearance', label: 'Import Clearance' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Insurance', label: 'Insurance' },
];

const RATE_UNITS: { value: RateUnit; label: string }[] = [
  { value: 'PerContainer', label: 'Per Container' },
  { value: 'PerCBM', label: 'Per CBM' },
  { value: 'PerKG', label: 'Per KG' },
  { value: 'PerMile', label: 'Per Mile' },
  { value: 'PerCWT', label: 'Per CWT' },
  { value: 'PerTon', label: 'Per Ton' },
  { value: 'PerPallet', label: 'Per Pallet' },
  { value: 'PerUnit', label: 'Per Unit' },
  { value: 'PerShipment', label: 'Per Shipment' },
  { value: 'Flat', label: 'Flat' },
  { value: 'Percentage', label: 'Percentage' },
];

export default function ChargeTypesNew() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateChargeTypeDto>({
    code: '',
    name: '',
    category: 'Freight',
    description: '',
    rateUnit: 'Flat',
    isForRFQ: true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await chargeTypesApi.create(formData);
      toast.success('Charge type created successfully');
      navigate('/tms/charge-types');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Add Charge Type"
        description="Code, category, and rate unit required to define a freight cost component."
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/charge-types')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Charge Types
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Charge Type Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Charge Type Details</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="FREIGHT-BASE"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData({ ...formData, category: value as ChargeCategoryExtended })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHARGE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Base Freight Charge"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe when this charge applies..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rateUnit">Rate Unit</Label>
              <Select
                value={formData.rateUnit}
                onValueChange={(value) => setFormData({ ...formData, rateUnit: value as RateUnit })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RATE_UNITS.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Include in RFQ</Label>
                <p className="text-sm text-muted-foreground">
                  Show this charge type in freight RFQ requests
                </p>
              </div>
              <Switch
                checked={formData.isForRFQ}
                onCheckedChange={(checked) => setFormData({ ...formData, isForRFQ: checked })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/tms/charge-types')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Charge Type'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
